import React from "react"

import { homeCardStyle, homeColor, homeText } from "../home-theme.ts"

/**
 * Edit layout's per-widget controls, split by where they sit in the header:
 * the drag handle leads the title, the layout menu and Remove close the row.
 */
export interface HomeCardEdit {
  handle: React.ReactNode
  controls: React.ReactNode
}

interface HomeCardHeaderProps {
  icon?: React.ReactNode
  title: React.ReactNode
  /** A quiet line beside the title — "As of today", a count. */
  meta?: React.ReactNode
  /** The card's own actions: View all, Refresh, Expand, Export. */
  actions?: React.ReactNode
  edit?: HomeCardEdit | null
}

/**
 * The one header every Home card shares.
 *
 * Cash Flow, Financial Snapshot and Recent activity used to draw three
 * different headers (an antd Card title, a widget frame, a bare heading), so
 * two cards side by side never lined up. One component, one height, one
 * padding: 12px 16px, a 28px glyph square, the title, then actions.
 */
export const HomeCardHeader = ({
  icon,
  title,
  meta,
  actions,
  edit
}: HomeCardHeaderProps) => (
  <div className="home-card__header">
    {edit?.handle}
    {icon ? (
      <span aria-hidden className="home-card__glyph">
        {icon}
      </span>
    ) : null}
    {/* `textAlign` is explicit: a shell-level rule centres bare headings. */}
    <h3
      className="home-card__title"
      style={{ ...homeText.itemTitle, color: homeColor.ink, margin: 0 }}
    >
      {title}
    </h3>
    {meta ? (
      <span
        className="home-card__meta"
        style={{ ...homeText.meta, color: homeColor.inkMuted }}
      >
        {meta}
      </span>
    ) : null}
    <span className="home-card__actions">
      {actions}
      {edit?.controls}
    </span>
  </div>
)

interface HomeCardProps extends HomeCardHeaderProps {
  testId?: string
  /** The body's own padding; 16px unless the content draws edge to edge. */
  bodyPadding?: number | string
  children?: React.ReactNode
}

/**
 * A Home card: the shared header over a body that fills whatever height its
 * grid cell gives it and scrolls inside itself rather than spilling past the
 * card's edge. Outside the grid (the stacked phone layout) it is simply as
 * tall as its content.
 */
export const HomeCard = ({
  testId,
  bodyPadding = 16,
  children,
  ...header
}: HomeCardProps) => (
  <section
    className="home-card"
    data-testid={testId}
    style={{ ...homeCardStyle }}
  >
    <HomeCardHeader {...header} />
    <div className="home-card__body" style={{ padding: bodyPadding }}>
      {children}
    </div>
  </section>
)
